'use client';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Volume2, VolumeX, Mic, MicOff, Smile, Frown, X, Sparkles, Send, Phone, MessageCircle, RefreshCw, CircleHelp, ChevronDown, LoaderCircle } from 'lucide-react';
import Bubble from './Bubble';
import type { ToolCallEvent } from './ToolCallChip';
import type { PresentationProps } from '@/embed/render/adapter';
import type { PresentationEvent } from '@/embed/postmessage/protocol';

interface Props {
  props: PresentationProps;
  emit: (e: PresentationEvent) => void;
}

// Same color-grading filter ChatPanel.tsx applies to its bounded avatar
// slot (Phase 2) — reused verbatim here, just applied full-bleed instead of
// to an internal region.
const AVATAR_FILTER = 'brightness(var(--mobeus-avatar-brightness, 0.72)) saturate(var(--mobeus-avatar-saturate, 0.9))';

// Momentum's chat-message-bubble, measured off the live site: white-glass
// fill over a heavy backdrop blur, 16px radius, soft drop shadow.
const GLASS_BUBBLE: CSSProperties = {
  padding: '10px 14px',
  borderRadius: 16,
  background: 'var(--mobeus-fs-bubble-bg, rgba(255,255,255,0.12))',
  backdropFilter: 'blur(32px)',
  WebkitBackdropFilter: 'blur(32px)',
  border: '1px solid var(--mobeus-fs-bubble-border, rgba(255,255,255,0.1))',
  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  color: 'var(--mobeus-fs-text, #e0e0e0)',
  fontSize: 14,
  lineHeight: 1.5,
};

const iconBtnBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  flexShrink: 0,
  border: '1px solid rgba(255,255,255,0.14)',
  background: 'rgba(0,0,0,0.35)',
  color: 'var(--mobeus-fs-text, #e7ecf3)',
  cursor: 'pointer',
  padding: 0,
};

// Small circular avatar-photo icon reused next to the message bubbles
// (Transcripts.tsx's message-avatar pattern, ported — see its own comment
// for why a null thumbnail renders a plain colored circle instead of a
// broken-image icon). 28px matches momentum's chat-avatar.
function AvatarThumb({ src }: { src?: string | null }) {
  const size = 28;
  return src ? (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className="mobeus-fs-avatar-thumb"
      part="fs-avatar-thumb"
      style={{
        width: size, height: size, borderRadius: '50%',
        objectFit: 'cover', flexShrink: 0,
        border: '1px solid rgba(255,255,255,0.2)',
      }}
    />
  ) : (
    <div
      aria-hidden="true"
      className="mobeus-fs-avatar-thumb-fallback"
      part="fs-avatar-thumb"
      style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: 'var(--mobeus-fs-primary, #1bb8a6)',
        border: '1px solid rgba(255,255,255,0.2)',
      }}
    />
  );
}

// Inline status rendered as an agent-side chat bubble — momentum's pattern
// for "Generating mobeus teléglass" / "Looking…": avatar thumb + glass
// bubble with a pulsing teal dot, living IN the message flow rather than
// as a floating chip.
function StatusBubble({ label, thumbSrc }: { label: string; thumbSrc?: string | null }) {
  return (
    <div
      className="mobeus-bubble-enter"
      style={{
        display: 'flex', flexDirection: 'row', alignSelf: 'flex-start',
        alignItems: 'flex-start', gap: 8, maxWidth: 'min(85%, 460px)',
      }}
    >
      <AvatarThumb src={thumbSrc} />
      <div
        className="mobeus-fs-status-bubble"
        part="fs-status-bubble"
        style={{ ...GLASS_BUBBLE, display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <span
          aria-hidden="true"
          className="mobeus-fs-status-dot"
          style={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            background: 'var(--mobeus-fs-primary, #1bb8a6)',
          }}
        />
        <span style={{ color: 'var(--mobeus-fs-text-muted, #9aa6b6)' }}>{label}</span>
      </div>
    </div>
  );
}

// Tool-call event rendered as a translucent dark dropdown pill (icon +
// monospace label + chevron), per the Phase 6 reference — visually distinct
// from the compact mode's `ToolCallChip.tsx` accordion styling, so this is
// a sibling component rather than a reuse. Icon/color choice is a heuristic
// (the reference shows two example tools — "update_agent" with a green
// refresh icon, "prompt_agent" with a teal question-mark icon — without
// guaranteeing those exact tool names ever appear, so we key off whether the
// name looks like a mutation rather than hardcoding those two strings).
function FullscreenToolChip({ event }: { event: ToolCallEvent }) {
  const [open, setOpen] = useState(false);
  const tool = event.tool ?? 'tool';
  const isMutation = /update|refresh|sync/i.test(tool);
  const Icon = isMutation ? RefreshCw : CircleHelp;
  const iconColor = isMutation ? '#34d399' : 'var(--mobeus-fs-primary, #2dd4bf)';
  return (
    <div
      className="mobeus-fs-tool-chip"
      part="fs-tool-chip"
      onClick={() => setOpen((v) => !v)}
      style={{
        padding: '8px 14px',
        borderRadius: 999,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(0,0,0,0.35)',
        color: 'var(--mobeus-fs-text, #e7ecf3)',
        fontSize: 12,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        cursor: 'pointer',
        maxWidth: '100%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon size={14} color={iconColor} aria-hidden="true" />
        <span>{tool}</span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`mobeus-fs-chip-chevron${open ? ' mobeus-open' : ''}`}
        />
      </div>
      {open && event.data && (
        <pre style={{
          margin: '8px 0 0', padding: 8, borderRadius: 8,
          background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.1)',
          fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          color: 'var(--mobeus-fs-text-muted, #9aa6b6)',
        }}>{JSON.stringify(event.data, null, 2)}</pre>
      )}
    </div>
  );
}

// Fullscreen avatar-takeover layout (Phase 6). Sibling to `ChatPanel.tsx`,
// not a variant of it — visually and structurally different enough (full-
// bleed video, absolutely-positioned floating overlays vs. a bounded panel
// with a scrolling transcript list) that composing them would mean more
// conditional branching inside ChatPanel than just keeping them separate.
// Reuses what's actually shared: the avatar-mount-element wiring pattern
// (`avatarInternalMount`/`data-mobeus-avatar-mount`, same as ChatPanel),
// the collapsed `Bubble` affordance, the `--mobeus-avatar-*` grading filter,
// and the `mobeus-bubble-enter`/`mobeus-panel-enter` keyframes from
// styles.css.
export default function FullscreenPanel({ props, emit }: Props) {
  const [text, setText] = useState('');

  // Keep the scrolling history pinned to the newest message. Lives above
  // the collapsed-state early return because hooks must run unconditionally.
  // isOverlayExpanded is a dependency because collapsing unmounts the
  // history element — on re-expand the transcripts reference hasn't
  // changed, so without it the fresh element would mount scrolled to the top.
  const historyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = historyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [props.transcripts, props.isOverlayExpanded]);

  // Collapsed state is identical to the compact panel — fullscreen only
  // changes what renders once the panel is opened (per PRD Phase 6).
  if (!props.isOverlayExpanded) {
    const showWarmupMount = props.isConnecting && props.avatarEnabled && props.avatarInternalMount;
    return (
      <>
        {showWarmupMount && (
          // The live avatar track needs a real DOM mount before the fullscreen
          // chat opens, otherwise waiting for avatarReady deadlocks.
          <div
            data-mobeus-avatar-mount
            aria-hidden="true"
            className="mobeus-fs-avatar-warmup"
            style={{
              position: 'fixed',
              right: 0,
              bottom: 0,
              width: 1,
              height: 1,
              overflow: 'hidden',
              opacity: 0,
              pointerEvents: 'none',
            }}
          />
        )}
        <Bubble onClick={() => emit({ type: 'toggleExpand' })} agentState={props.agentState} loading={props.isConnecting}
          error={props.inputStart ? props.error?.message : undefined}
          onSubmit={props.inputStart ? (text) => emit({ type: 'startConversation', text }) : undefined} />
      </>
    );
  }

  const sendText = () => {
    if (!text.trim()) return;
    emit({ type: 'sendText', text: text.trim() });
    setText('');
  };

  // Same idle-background source as ChatPanel's Phase 2 crossfade
  // (`avatarThumbnailUrl` from `prepared.widget.defaults`), just filling the
  // whole viewport instead of a bounded panel region.
  const idleBackground = props.avatarThumbnailUrl
    ? `url(${props.avatarThumbnailUrl}) center / cover no-repeat`
    : 'radial-gradient(circle at 60% 20%, #16303a 0%, #0a0e14 70%)';

  // Fullscreen layout is gated in bootstrap.ts to require avatar:true +
  // render:"shadow", so this is normally always true whenever this
  // component actually renders — kept as an explicit check anyway (same
  // pattern ChatPanel.tsx uses) so a defensively-misconfigured embed just
  // shows the idle background rather than an empty mount div.
  const showAvatarMount = props.avatarEnabled && props.avatarInternalMount;

  const toolChips = (props.showToolCalls ? (props.toolActivity as ToolCallEvent[]) : []).slice(-2);

  return (
    <div
      className="mobeus-fs-root mobeus-panel-enter"
      part="fs-root"
      style={{
        // Self-anchored to the viewport rather than relying on `height:100%`
        // cascading through the shadow root's mount <div> (which is
        // deliberately unstyled/unsized — see render/shadow.ts) and `host`'s
        // own sizing. A percentage height needs every ancestor in the chain
        // to have a definite (non-auto) height; that chain doesn't reliably
        // hold across a closed shadow root, so don't depend on it.
        //
        // Docked mode (`props.docked`) is the one exception: `host` itself
        // is already `position:absolute` sized to fill the dock container
        // (render/shadow.ts), which — being a real positioned ancestor, not
        // an intermediate unsized flow element — IS a reliable containing
        // block. `100vw`/`100vh` would still be viewport units regardless of
        // that positioning, so they'd overflow a smaller container; use
        // `100%` (resolves against `host`) instead.
        position: props.docked ? 'absolute' : 'fixed',
        inset: 0,
        width: props.docked ? '100%' : '100vw',
        height: props.docked ? '100%' : '100vh',
        overflow: 'hidden',
        background: '#000',
        fontFamily: 'var(--mobeus-font, system-ui, sans-serif)',
        color: 'var(--mobeus-fs-text, #e7ecf3)',
      }}
    >
      {/* Scoped hover/focus/disabled states — same pattern Controls.tsx uses
       * for its icon buttons, inline styles can't express pseudo-classes. */}
      <style>{`
        .mobeus-fs-icon-btn {
          transition: opacity 0.15s ease, transform 0.1s ease, background 0.15s ease;
        }
        .mobeus-fs-icon-btn:hover:not(:disabled) { opacity: 0.85; }
        .mobeus-fs-icon-btn:active:not(:disabled) { transform: scale(0.95); }
        .mobeus-fs-icon-btn:disabled { opacity: 0.4; cursor: default; }
        .mobeus-fs-start-btn { transition: opacity 0.15s ease, transform 0.1s ease; }
        .mobeus-fs-start-btn:hover:not(:disabled) { opacity: 0.9; }
        .mobeus-fs-start-btn:active:not(:disabled) { transform: scale(0.97); }
        .mobeus-fs-send-btn { transition: opacity 0.15s ease, transform 0.1s ease; }
        .mobeus-fs-send-btn:hover:not(:disabled) { opacity: 0.9; }
        .mobeus-fs-send-btn:active:not(:disabled) { transform: scale(0.95); }
        .mobeus-fs-input::placeholder { color: var(--mobeus-fs-text-muted, #9aa6b6); }
        .mobeus-fs-input { background: transparent; border: none; outline: none; }
        .mobeus-fs-tool-chip:hover { background: rgba(0,0,0,0.5) !important; }
        @keyframes mobeus-spin { to { transform: rotate(360deg); } }
        .mobeus-spinner { animation: mobeus-spin 0.8s linear infinite; }
      `}</style>

      {/* Full-bleed idle background image/gradient, sized to the whole
        * viewport instead of a bounded region. `--mobeus-fs-bg` lets a
        * partner supply their own full-bleed art (momentum's hero image)
        * via styling.vars instead of falling back to the avatar thumb.
        * The avatar "breathes" (brightness pulse, momentum's
        * avatar-video-pulse) while the agent is thinking or speaking.
        *
        * This layer stays opaque and acts as the BACKDROP rather than
        * crossfading out: the avatar mount below is a later sibling in the
        * same stacking context, so its video paints over this once it has
        * frames (builtins/avatar.ts fades it in on 'playing'), which is the
        * crossfade. Fading this out on `showAvatarMount` instead — as it
        * used to — blanked the panel ~2.5s BEFORE the <video> element was
        * even created, so a cold start showed 2.7-3.3s of black where the
        * art had been ("the girl disappears on connect", measured on
        * injuryinfo.webflow.io). Keeping it also means a partner whose
        * avatar never connects degrades to their art instead of black. */}
      <div
        className={`mobeus-fs-idle${props.agentState === 'thinking' || props.agentState === 'speaking' ? ' mobeus-fs-avatar-pulse' : ''}`}
        part="fs-idle"
        style={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: `var(--mobeus-fs-bg, ${idleBackground})`,
          opacity: 1,
          filter: AVATAR_FILTER,
          transition: 'opacity 0.6s ease, filter 0.6s ease',
          pointerEvents: 'none',
        }}
      />
      {/* Full-bleed avatar video mount — same `data-mobeus-avatar-mount`
        * attach point `installAvatarBuiltin` looks for via
        * `RenderAdapter.getAvatarMountEl()`, just laid out full-viewport. */}
      {showAvatarMount && (
        <div
          data-mobeus-avatar-mount
          className={`mobeus-fs-avatar-video${props.agentState === 'thinking' || props.agentState === 'speaking' ? ' mobeus-fs-avatar-pulse' : ''}`}
          part="fs-avatar-video"
          style={{
            position: 'absolute', inset: 0, zIndex: 0,
            opacity: props.avatarVisible ? 1 : 0,
            filter: AVATAR_FILTER,
            transition: 'opacity 0.6s ease, filter 0.6s ease',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Teal radial "thinking" glow breathing over the avatar — port of
        * momentum's avatar-thinking-glow overlay. */}
      {props.agentState === 'thinking' && (
        <div
          aria-hidden="true"
          className="mobeus-fs-thinking-glow"
          part="fs-thinking-glow"
          style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}
        />
      )}
      {/* Subtle top/bottom scrim so the control bar and input pill stay
        * legible over bright avatar video, without darkening the whole
        * frame the way the compact panel's opaque surface would. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 18%, transparent 72%, rgba(0,0,0,0.45) 100%)',
        }}
      />

      {/* Connect/runtime error banner — same information Phase 1 surfaces in
        * the compact ChatPanel (props.error), so a fullscreen session that
        * fails to connect doesn't just sit at "Connecting..." forever with
        * no explanation, silently. Stacked with the mic-permission notice
        * below (both share this positioned wrapper) rather than each
        * self-positioning, so the two never overlap if both are showing. */}
      {(props.error || props.micWarning) && (
        <div style={{
          position: 'absolute', top: 64, left: 16, right: 16, zIndex: 3,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {props.error && (
            <div
              className="mobeus-fs-error"
              part="fs-error"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                padding: '10px 16px', borderRadius: 12,
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid var(--mobeus-fs-danger, #f87171)',
                color: 'var(--mobeus-fs-text, #e7ecf3)',
                fontSize: 13,
              }}
            >
              <span>{props.error.message}</span>
              <button
                type="button"
                onClick={() => emit({ type: 'connect' })}
                aria-label="Retry"
                style={{
                  background: 'none', border: 'none', flexShrink: 0,
                  color: 'var(--mobeus-fs-danger, #f87171)', textDecoration: 'underline',
                  cursor: 'pointer', fontSize: 13,
                }}
              >Retry</button>
            </div>
          )}
          {/* Mic-permission notice — deliberately NOT styled like the error
            * banner above. `micWarning` fires on every connect attempt (see
            * bootstrap.ts's eager mic-enable call), including for text-only
            * users who simply decline the browser's permission prompt; it's
            * not proof anything is actually broken, and text chat keeps
            * working regardless. */}
          {props.micWarning && (
            <div
              className="mobeus-fs-mic-warning"
              part="fs-mic-warning"
              style={{
                padding: '10px 16px', borderRadius: 12,
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid var(--mobeus-fs-warn, #eab308)',
                color: 'var(--mobeus-fs-text, #e7ecf3)',
                fontSize: 13,
              }}
            >
              Microphone access wasn&apos;t granted — you can still type below.
            </div>
          )}
        </div>
      )}

      {/* Top-left version badge — static/decorative, per PRD Phase 6 ("just
        * render a static small badge, doesn't need to be dynamic"). */}
      <div
        className="mobeus-fs-badge"
        part="fs-badge"
        style={{
          position: 'absolute', top: 16, left: 16, zIndex: 2,
          padding: '4px 10px', borderRadius: 999,
          border: '1px dashed rgba(255,255,255,0.35)',
          background: 'rgba(0,0,0,0.35)',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 11, letterSpacing: 0.5,
          color: 'var(--mobeus-fs-text-muted, #9aa6b6)',
        }}
      >
        V1.0
      </div>

      {/* Top-right control bar: volume, mic, avatar-visibility, close. */}
      <div
        className="mobeus-fs-controls"
        part="fs-controls"
        style={{ position: 'absolute', top: 16, right: 16, zIndex: 2, display: 'flex', gap: 8 }}
      >
        {/* Voice-only controls. In chat mode bootstrap.ts installs no audio
          * or avatar built-in and never publishes a mic track, so these
          * would toggle flags with nothing behind them. */}
        {!props.chatMode && (
          <>
        <button
          type="button"
          className="mobeus-fs-icon-btn"
          part="fs-volume-button"
          onClick={() => emit({ type: 'toggleVolume' })}
          aria-label={props.isVolumeMuted ? 'Unmute audio' : 'Mute audio'}
          title={props.isVolumeMuted ? 'Unmute audio' : 'Mute audio'}
          style={iconBtnBase}
        >
          {props.isVolumeMuted ? <VolumeX size={18} aria-hidden="true" /> : <Volume2 size={18} aria-hidden="true" />}
        </button>
        <button
          type="button"
          className="mobeus-fs-icon-btn"
          part="fs-mic-button"
          onClick={() => emit({ type: 'toggleMute' })}
          disabled={!props.isConnected}
          aria-label={props.isMuted ? 'Unmute microphone' : 'Mute microphone'}
          title={props.isMuted ? 'Unmute microphone' : 'Mute microphone'}
          style={iconBtnBase}
        >
          {props.isMuted ? <MicOff size={18} aria-hidden="true" /> : <Mic size={18} aria-hidden="true" />}
        </button>
        <button
          type="button"
          className="mobeus-fs-icon-btn"
          part="fs-visibility-button"
          onClick={() => emit({ type: 'toggleAvatarVisible' })}
          aria-label={props.avatarVisible ? 'Hide avatar' : 'Show avatar'}
          title={props.avatarVisible ? 'Hide avatar' : 'Show avatar'}
          style={iconBtnBase}
        >
          {props.avatarVisible ? <Smile size={18} aria-hidden="true" /> : <Frown size={18} aria-hidden="true" />}
        </button>
          </>
        )}
        <button
          type="button"
          className="mobeus-fs-icon-btn mobeus-fs-close-btn"
          part="fs-close-button"
          onClick={() => emit({ type: 'close' })}
          aria-label="End call and close"
          title="End call and close"
          style={{ ...iconBtnBase, background: 'var(--mobeus-fs-danger, #f87171)', border: 'none' }}
        >
          <X size={18} color="#fff" aria-hidden="true" />
        </button>
      </div>

      {/* Status / tool-call chips pinned at the top, full scrolling
        * conversation history beneath — matches the hosted sites' connected
        * chat, where the user's question and the agent's answer stay
        * visible together for the whole session. (Originally a single
        * floating latest-message bubble; each turn erased the previous one,
        * which read as lost history and scrambled ordering.) */}
      <div
        className="mobeus-fs-stack"
        part="fs-stack"
        style={{
          position: 'absolute', top: 72, left: 16, right: 16, bottom: 88, zIndex: 2,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        {/* Tool chips pinned top-right, same position momentum shows its
          * update_agent / prompt_agent pills. No status chip up here —
          * momentum surfaces connecting/thinking as inline chat bubbles
          * (see below), and 'speaking' shows only as the avatar pulse. */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {/* Key by position in the FULL activity list, not the slice — a
            * slice-local index shifts when a new event pushes an older chip
            * out, remounting the survivor (resetting its open state and
            * replaying its entrance). */}
          {toolChips.map((event, i) => (
            <FullscreenToolChip
              key={`fs-tool-${props.toolActivity.length - toolChips.length + i}-${event.timestamp ?? ''}`}
              event={event}
            />
          ))}
        </div>

        <div
          ref={historyRef}
          className="mobeus-fs-history"
          part="fs-history"
          style={{
            flex: 1, minHeight: 0, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 10,
            // Momentum's chat-messages-container top fade — messages
            // dissolve as they scroll up under the control bar.
            maskImage: 'linear-gradient(to bottom, transparent 0px, black 72px)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0px, black 72px)',
            scrollbarWidth: 'none',
          }}
        >
          {props.transcripts.map((entry) => {
            const isAgent = entry.participant === 'agent';
            return (
              <div
                key={entry.id}
                className="mobeus-bubble-enter"
                style={{
                  display: 'flex',
                  flexDirection: isAgent ? 'row' : 'row-reverse',
                  alignSelf: isAgent ? 'flex-start' : 'flex-end',
                  alignItems: 'flex-start',
                  gap: 8,
                  maxWidth: 'min(85%, 460px)',
                }}
              >
                <AvatarThumb src={isAgent ? props.avatarThumbnailUrl : null} />
                <div
                  className="mobeus-fs-message"
                  part="fs-message"
                  style={{
                    ...GLASS_BUBBLE,
                    opacity: entry.isFinal ? 1 : 0.75,
                  }}
                >
                  {entry.text}
                </div>
              </div>
            );
          })}

          {/* Inline status bubbles, momentum-style ("Generating mobeus
            * teléglass" / "Looking…" render as chat bubbles, not chips). */}
          {(props.isConnecting || (props.isConnected && props.agentState === 'initializing')) && (
            <StatusBubble label="Connecting…" thumbSrc={props.avatarThumbnailUrl} />
          )}
          {props.isConnected && props.agentState === 'thinking' && (
            <StatusBubble label="Looking…" thumbSrc={props.avatarThumbnailUrl} />
          )}
        </div>
      </div>

      {/* Bottom input pill — same glass-pill visual treatment as the
        * collapsed Bubble.tsx affordance, full-width instead of
        * intrinsic-width. */}
      <div
        className="mobeus-fs-input-row"
        part="fs-input-row"
        style={{
          position: 'absolute', left: 24, right: 24, bottom: 24, zIndex: 2,
          display: 'flex', alignItems: 'center', gap: 10,
          height: 52, padding: '0 8px 0 18px',
          borderRadius: 999,
          background: 'var(--mobeus-fs-glass-bg, rgba(255,255,255,0.1))',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          border: '1px solid var(--mobeus-fs-glass-border, rgba(255,255,255,0.12))',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        }}
      >
        <Sparkles size={16} color="var(--mobeus-fs-primary, #1bb8a6)" aria-hidden="true" />
        <input
          className="mobeus-fs-input"
          part="fs-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // isComposing: an IME (Japanese/Chinese/Korean) commit also
            // fires Enter — that must finish the composition, not send.
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) sendText();
          }}
          placeholder="Type a message..."
          style={{ flex: 1, color: 'var(--mobeus-fs-text, #e7ecf3)', fontSize: 14, fontFamily: 'inherit' }}
        />
        {/* Start affordance. The fullscreen layout previously had NO way to
          * open a session — its only `connect` emitter was the Retry link
          * inside the error banner — so turning auto-start off left a widget
          * you could not start. Shown in chat mode too: "just type, it
          * connects" is invisible, and after `close` sets `userEndedSession`
          * the collapsed bubble no longer auto-connects either, so hiding it
          * left chat+fullscreen with no start control at all once a visitor
          * had hung up. */}
        {!props.isConnected && (
          <button
            type="button"
            className={`mobeus-fs-start-btn${props.isConnecting ? ' mobeus-connecting-pulse' : ''}`}
            part="fs-start-button"
            onClick={() => emit({ type: 'connect' })}
            disabled={props.isConnecting}
            aria-busy={props.isConnecting ? 'true' : undefined}
            aria-label={props.isConnecting ? 'Connecting' : props.chatMode ? 'Start chat' : 'Start call'}
            title={props.isConnecting ? 'Connecting…' : props.chatMode ? 'Start chat' : 'Start call'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              height: 36, padding: '0 14px', borderRadius: 999, flexShrink: 0, border: 'none',
              cursor: props.isConnecting ? 'default' : 'pointer',
              background: 'var(--mobeus-fs-primary, #1bb8a6)', color: '#062521',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              opacity: props.isConnecting ? 0.6 : 1,
            }}
          >
            {props.isConnecting
              ? <LoaderCircle className="mobeus-spinner" size={14} aria-hidden="true" />
              : props.chatMode
                ? <MessageCircle size={14} aria-hidden="true" />
                : <Phone size={14} aria-hidden="true" />}
            {props.isConnecting ? 'Connecting…' : 'Start'}
          </button>
        )}
        <button
          type="button"
          className="mobeus-fs-send-btn"
          part="fs-send-button"
          onClick={sendText}
          disabled={!text.trim()}
          aria-label="Send message"
          title="Send"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0, border: 'none',
            cursor: text.trim() ? 'pointer' : 'default',
            background: 'var(--mobeus-fs-primary, #1bb8a6)', color: '#062521',
            opacity: text.trim() ? 1 : 0.5,
          }}
        >
          <Send size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
